import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  typedInfiniteQuery,
  typedMutation,
  typedQuery,
} from "shared/api/types/typedQueries";
import useToastes from "shared/hooks/useToastes";
import {
  ServicesT,
  CreateServiceT,
  OneServiceT,
  ServiceModuleT,
  OneSettingT,
  UpdateSettingT,
  OneActorT,
  UpdateActorT,
  OneGroupT,
  UpdateGroupT,
  CreateGroupT,
  DeleteGroupT,
  ToggleGroupT,
  ServiceOriginalModuleT,
  PaginationT,
  DTO,
  CreateActorT,
  OneActorSettingT,
  UpdateActorSettingT,
  CreateActorSettingT,
  DeleteServiceT,
  UpdateActorPhotoT,
} from "../types/services";
import {
  bareInstance,
  camelizeResponse,
  formDataApi,
  instance,
  paginationReponseToGeneral,
} from "./common";
import { LayoutsE, ModuleE, PathsE, PathsParams } from "shared/routes";
import useTypedHistory from "shared/hooks/useTypedHistory";
import { RoleE } from "../types/auth";
import {
  AddDialogMessage,
  ApplyEditDialogMessageT,
  ChangeOrderMessageT,
  DeleteDialogMessagesT,
  DeleteDialogMessageT,
  DialogMessagesT,
  RegenerateDialogMessageT,
  SkipDialogMessageT,
  UpdateMessageT,
} from "../types/dialog_messages";
import { useEffect } from "react";

const BASE_URL = "services";

export const SERVICES_KEY = "SERVICES_KEY";
const ONE_SERVICE_KEY = "ONE_SERVICE_KEY";

// const ONE_PROMPT_KEY = 'ONE_PROMPT_KEY'
// const ONE_SETTING_KEY = 'ONE_SETTING_KEY'
const ONE_ACTOR_KEY = "ONE_ACTOR_KEY";
const ONE_GROUP_KEY = "ONE_GROUP_KEY";
const ONE_ACTOR_SETTING_KEY = "ONE_ACTOR_SETTING_KEY";

const getFormData = (data: Record<string, string | Blob>) => {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value != null) {
      formData.append(key, value);
    }
  });
  return formData;
}

const getWSUrl = (service: string, token: string, channel: string) => {
  const domain = service.replace("https://", "wss://");
  return `${domain}/ws/dialog?token=${token}&channel=${channel}`;
};

export const useGetServices = typedInfiniteQuery<ServicesT>(({ params }) =>
  useInfiniteQuery({
    queryKey: [SERVICES_KEY, params],
    queryFn: () =>
      instance.get<unknown, ServicesT["response"]>(BASE_URL, { params }),
    initialPageParam: 1,
    getNextPageParam: () => null,
    getPreviousPageParam: () => null,
  }),
);

export const useGetOneService = typedQuery<OneServiceT>(
  ({ pathParams: { serviceId } }) =>
    useQuery({
      queryKey: [ONE_SERVICE_KEY, serviceId],
      queryFn: () => instance.get(`${BASE_URL}/${serviceId}`),
      enabled: !!serviceId,
    }),
);

export const useCreateService = typedMutation<CreateServiceT>(() => {
  const queryCache = useQueryClient();
  const { successToast } = useToastes();
  return useMutation({
    mutationFn: (data) => instance.post(BASE_URL, data),
    onSuccess: () => {
      successToast("Service was successfully created");
      queryCache.invalidateQueries({ queryKey: [SERVICES_KEY] });
    },
  });
});
export const useDeleteService = typedMutation<DeleteServiceT>(() => {
  const queryCache = useQueryClient();
  const { successToast } = useToastes();
  return useMutation({
    mutationFn: ({ serviceId }) => instance.delete(`${BASE_URL}/${serviceId}`),
    onSuccess: () => {
      successToast("Service was successfully deleted");
      queryCache.invalidateQueries({ queryKey: [SERVICES_KEY] });
    },
  });
});

// MODULES
type ServicesMainParamsT = {
  domain: string;
  token: string;
  module?: ModuleE;
};

export const useGet3thPartyServices = ({
  domain,
  token,
  module,
}: ServicesMainParamsT) => ({
  useGetServiceModule: typedInfiniteQuery<ServiceModuleT>(({ params }) =>
    useInfiniteQuery({
      queryKey: [module, token, domain, params],
      queryFn: () =>
        bareInstance.get<unknown, ServiceOriginalModuleT["response"]>(
          `${domain}/${module}/`,
          { headers: { "x-service-token": token }, params },
        ),
      enabled: !!(token && domain && module),
      initialPageParam: 1,
      getNextPageParam: () => null,
      getPreviousPageParam: () => null,
      select: (data) => {
        if (module === ModuleE.GROUPS) {
          return {
            pageParams: data.pageParams,
            pages: data.pages.map((page) => {
              return {
                data: page as unknown as DTO[],
                page: 0,
                size: 20,
                total: data.pages.length,
              };
            }),
          };
        }
        return paginationReponseToGeneral(data);
      },
    }),
  ),

  // PROMPTS
  // useGetOnePrompt: typedQuery<OnePromptT>(({ pathParams: { promptId } }) => (
  //   useQuery({
  //     queryKey: [ONE_PROMPT_KEY, promptId],
  //     queryFn: () => bareInstance.get(
  //       `${domain}/prompts/id/${promptId}`,
  //       { headers: { 'x-service-token': token } }
  //     ),
  //     enabled: !!promptId && !!domain && !!token,
  //   })
  // )),

  // useUpdateOnePrompt: typedMutation<UpdatePromptT>(({ pathParams: { promptId, serviceId } }) => {
  //   const { successToast } = useToastes();
  //   const queryCache = useQueryClient()
  //   const push = useTypedHistory<PathsParams[PathsE.SHOW_PROMPT]>();
  //   return (
  //     useMutation({
  //       mutationFn: (data) => bareInstance.put(
  //         `${domain}/prompts/id/${promptId}`,
  //         data,
  //         { headers: { 'x-service-token': token } }
  //       ),
  //       onSuccess: () => {
  //         successToast('Prompt was successfully updated');
  //         queryCache.invalidateQueries({ queryKey: [ModuleE.PROMPTS] });
  //         queryCache.invalidateQueries({ queryKey: [ONE_PROMPT_KEY, promptId] })
  //         push(PathsE.PROMPTS, LayoutsE.DASHBOARD, { serviceId, promptId })
  //       }
  //     })
  //   )
  // }),

  // ACTOR SETTINGS
  useGetOneActorSetting: typedQuery<OneActorSettingT>(
    ({ pathParams: { actorSettingId } }) =>
      useQuery({
        queryKey: [ONE_ACTOR_SETTING_KEY, actorSettingId],
        queryFn: () =>
          bareInstance.get(`${domain}/promptsettings/id/${actorSettingId}`, {
            headers: { "x-service-token": token },
          }),
        enabled: !!actorSettingId && !!domain && !!token,
      }),
  ),

  useCreateOneActorSetting: typedMutation<CreateActorSettingT>(
    ({ pathParams: { serviceId }, params }) => {
      const { successToast } = useToastes();
      const queryCache = useQueryClient();
      const push = useTypedHistory<PathsParams[PathsE.SETTINGS]>();
      return useMutation({
        mutationFn: (data) =>
          bareInstance.post(`${domain}/promptsettings/`, data, {
            headers: { "x-service-token": token },
          }),
        onSuccess: () => {
          successToast("Actor settings was successfully created");
          queryCache.invalidateQueries({ queryKey: [ModuleE.PROMPT_SETTINGS] });
          push(PathsE.SETTINGS, LayoutsE.DASHBOARD, { serviceId }, params);
        },
      });
    },
  ),

  useUpdateOneActorSetting: typedMutation<UpdateActorSettingT>(
    ({ pathParams: { actorSettingId, serviceId }, params }) => {
      const { successToast } = useToastes();
      const queryCache = useQueryClient();
      const push = useTypedHistory<PathsParams[PathsE.SETTINGS]>();
      return useMutation({
        mutationFn: (data) =>
          bareInstance.put(
            `${domain}/promptsettings/id/${actorSettingId}`,
            data,
            { headers: { "x-service-token": token } },
          ),
        onSuccess: () => {
          successToast("Actor settings was successfully updated");
          queryCache.invalidateQueries({ queryKey: [ModuleE.PROMPT_SETTINGS] });
          queryCache.invalidateQueries({
            queryKey: [ONE_ACTOR_SETTING_KEY, actorSettingId],
          });
          push(PathsE.SETTINGS, LayoutsE.DASHBOARD, { serviceId }, params);
        },
      });
    },
  ),

  // SETTINGS
  useGetSettings: (role: RoleE) =>
    typedInfiniteQuery<ServicesT>(({ params }) =>
      useInfiniteQuery({
        queryKey: [ModuleE.SETTINGS, role, token, domain, params],
        queryFn: () =>
          bareInstance.get<unknown, ServiceOriginalModuleT["response"]>(
            `${domain}/${module}/`,
            {
              headers: { "x-service-token": token },
              params: { ...params, role: role === "admin" ? "admin" : "user" },
            },
          ),
        initialPageParam: 1,
        getNextPageParam: () => null,
        getPreviousPageParam: () => null,
        select: (data) => {
          console.log('settings data', data);
          const result = {
            ...data,
            pages: data.pages.map((page) => {
              const pagination = camelizeResponse(page.pagination) as PaginationT;
              return {
                data: page.items,
                page: pagination.currentPage,
                size: pagination.perPage,
                total: pagination.totalItems,
              };
            }),
          };
          console.log('result', result);
          return result
        },
      }),
    ),

  useGetOneSetting: typedQuery<OneSettingT>(({ pathParams: { settingId } }) =>
    useQuery({
      queryKey: [ModuleE.SETTINGS, settingId],
      queryFn: () =>
        bareInstance.get(`${domain}/settings/id/${settingId}`, {
          headers: { "x-service-token": token },
        }),
      enabled: !!settingId && !!domain && !!token,
    }),
  ),

  useUpdateOneSetting: typedMutation<UpdateSettingT>(
    ({ pathParams: { settingId, serviceId }, params }) => {
      const { successToast } = useToastes();
      const queryCache = useQueryClient();
      const push = useTypedHistory<PathsParams[PathsE.SHOW_SETTING]>();
      return useMutation({
        mutationFn: (data) =>
          bareInstance.put(`${domain}/settings/id/${settingId}`, data, {
            headers: { "x-service-token": token },
          }),
        onSuccess: () => {
          successToast("Setting was successfully updated");
          queryCache.invalidateQueries({ queryKey: [ModuleE.SETTINGS] });
          queryCache.invalidateQueries({
            queryKey: [ModuleE.SETTINGS, settingId],
          });
          push(
            PathsE.SETTINGS,
            LayoutsE.DASHBOARD,
            { serviceId, settingId },
            params,
          );
        },
      });
    },
  ),

  // ACTORS
  useGetOneActor: typedQuery<OneActorT>(({ pathParams: { actorId } }) =>
    useQuery({
      queryKey: [ONE_ACTOR_KEY, actorId],
      queryFn: () =>
        bareInstance.get(`${domain}/actors/id/${actorId}`, {
          headers: { "x-service-token": token },
        }),
      enabled: !!actorId && !!domain && !!token,
    }),
  ),

  useCreateActor: typedMutation<CreateActorT>(
    ({ pathParams: { serviceId }, params }) => {
      const queryCache = useQueryClient();
      const { successToast } = useToastes();
      const push = useTypedHistory<PathsParams[PathsE.ACTORS]>();
      return useMutation({
        mutationFn: (data) =>
          bareInstance.post(`${domain}/actors/`, data, {
            headers: { "x-service-token": token },
          }),
        onSuccess: () => {
          successToast("Actor was successfully created");
          queryCache.invalidateQueries({ queryKey: [ModuleE.ACTORS] });
          push(PathsE.ACTORS, LayoutsE.DASHBOARD, { serviceId }, params);
        },
      });
    },
  ),

  useUpdateOneActor: typedMutation<UpdateActorT>(
    ({ pathParams: { actorId, serviceId }, params }) => {
      const { successToast } = useToastes();
      const queryCache = useQueryClient();
      const push = useTypedHistory<PathsParams[PathsE.SHOW_ACTOR]>();
      return useMutation({
        mutationFn: (data) => {
          const formData = getFormData(data);
          return  formDataApi.put(`${domain}/actors/${actorId}/edit`, formData, {
            headers: { "x-service-token": token },
          })
        },
        onSuccess: () => {
          successToast("Actor was successfully updated");
          queryCache.invalidateQueries({ queryKey: [ModuleE.ACTORS] });
          queryCache.invalidateQueries({ queryKey: [ModuleE.ACTORS, actorId] });
          queryCache.invalidateQueries({ queryKey: [ONE_ACTOR_KEY, actorId] }); // Добавил инвалидацию данного ключа, так как при повторном открытии окна edit, оставались старые данные
          push(
            PathsE.ACTORS,
            LayoutsE.DASHBOARD,
            { serviceId, actorId },
            params,
          );
        },
      });
    },
  ),

  useAddActorPhoto: typedMutation<UpdateActorPhotoT>(
    ({ pathParams: { actorId } }) => {
      return useMutation({
        mutationFn: (data) =>
          formDataApi.post(`${domain}/actors/${actorId}/photo`, data, {
            headers: { "x-service-token": token },
          }),
      });
    },
  ),

  // GROUPS
  useGetOneGroup: typedQuery<OneGroupT>(({ pathParams: { groupId } }) =>
    useQuery({
      queryKey: [ONE_GROUP_KEY, groupId],
      queryFn: () =>
        bareInstance.get(`${domain}/groups/id/${groupId}`, {
          headers: { "x-service-token": token },
        }),
      enabled: !!groupId && !!domain && !!token,
    }),
  ),

  useUpdateOneGroup: typedMutation<UpdateGroupT>(
    ({ pathParams: { groupId, serviceId }, params }) => {
      const { successToast } = useToastes();
      const queryCache = useQueryClient();
      const push = useTypedHistory<PathsParams[PathsE.SHOW_GROUP]>();
      return useMutation({
        mutationFn: (data) =>
          bareInstance.put(`${domain}/groups/id/${groupId}`, data, {
            headers: { "x-service-token": token },
          }),
        onSuccess: () => {
          successToast("Group was successfully updated");
          queryCache.invalidateQueries({ queryKey: [ModuleE.GROUPS] });
          queryCache.invalidateQueries({ queryKey: [ModuleE.GROUPS, groupId] });
          push(
            PathsE.GROUPS,
            LayoutsE.DASHBOARD,
            { serviceId, groupId },
            params,
          );
        },
      });
    },
  ),

  useCreateGroup: typedMutation<CreateGroupT>(
    ({ pathParams: { serviceId }, params }) => {
      const queryCache = useQueryClient();
      const { successToast } = useToastes();
      const push = useTypedHistory<PathsParams[PathsE.GROUPS]>();
      return useMutation({
        mutationFn: (data) =>
          bareInstance.post(`${domain}/groups/add`, data, {
            headers: { "x-service-token": token },
          }),
        onSuccess: () => {
          successToast("Group was successfully created");
          queryCache.invalidateQueries({ queryKey: [ModuleE.GROUPS] });
          push(PathsE.GROUPS, LayoutsE.DASHBOARD, { serviceId }, params);
        },
      });
    },
  ),

  useToggleGroup: typedMutation<CreateGroupT>(
    ({ pathParams: { serviceId }, params }) => {
      const queryCache = useQueryClient();
      const { successToast } = useToastes();
      const push = useTypedHistory<PathsParams[PathsE.GROUPS]>();
      return useMutation({
        mutationFn: (data) =>
          bareInstance.put(`${domain}/groups/add`, data, {
            headers: { "x-service-token": token },
          }),
        onSuccess: () => {
          successToast("Group was successfully created");
          queryCache.invalidateQueries({ queryKey: [ModuleE.GROUPS] });
          push(PathsE.GROUPS, LayoutsE.DASHBOARD, { serviceId }, params);
        },
      });
    },
  ),

  useDeleteGroup: typedMutation<DeleteGroupT>(
    ({ pathParams: { serviceId }, params }) => {
      const queryCache = useQueryClient();
      const { successToast } = useToastes();
      const push = useTypedHistory<PathsParams[PathsE.GROUPS]>();
      return useMutation({
        mutationFn: ({ groupId }) =>
          bareInstance.delete(`${domain}/groups/${groupId}`, {
            headers: { "x-service-token": token },
          }),
        onSuccess: () => {
          successToast("Group was successfully deleted");
          queryCache.invalidateQueries({ queryKey: [ModuleE.GROUPS] });
          push(PathsE.GROUPS, LayoutsE.DASHBOARD, { serviceId }, params);
        },
      });
    },
  ),

  useToggleOneGroup: typedMutation<ToggleGroupT>(() => {
    const { successToast } = useToastes();
    const queryCache = useQueryClient();
    return useMutation({
      mutationFn: ({ isActive, groupId }) =>
        bareInstance.put(
          `${domain}/groups/id/${groupId}`,
          { isActive },
          { headers: { "x-service-token": token } },
        ),
      onSuccess: () => {
        successToast("Group was successfully updated");
        queryCache.invalidateQueries({ queryKey: [ModuleE.GROUPS] });
      },
    });
  }),

  // DIALOG MESSAGES
  useGetDialogMessages: typedInfiniteQuery<DialogMessagesT>(({ params }) => {
    return useInfiniteQuery({
      queryKey: [ModuleE.DIALOG_MESSAGES, token, domain, params],
      queryFn: () =>
        bareInstance.get<unknown, DialogMessagesT["response"]>(
          `${domain}/dialogmessages/today/`,
          { headers: { "x-service-token": token }, params: params },
        ),
      enabled: !!domain,
      initialPageParam: 1,
      getNextPageParam: () => null,
      getPreviousPageParam: () => null,
      select: (data) => paginationReponseToGeneral(data as FixMeLater),
    });
  }),
  useGetSocketDialogMessages: typedInfiniteQuery<DialogMessagesT>(
    ({ params }) => {
      const queryClient = useQueryClient();
      useEffect(() => {
        const ws = new WebSocket(getWSUrl(domain, token, "dialog"));

        ws.onopen = () => {
          console.warn("Dialog messages socket connection opened");
        };
        ws.onmessage = () => {
          queryClient.invalidateQueries({
            queryKey: [ModuleE.DIALOG_MESSAGES],
          });
        };
        ws.onclose = () => {
          console.warn("Dialog messages socket connection closed");
        };

        return () => ws.close();
      }, [token, domain]);

      return useInfiniteQuery({
        queryKey: [ModuleE.DIALOG_MESSAGES, token, domain, params],
        queryFn: () =>
          bareInstance.get<unknown, DialogMessagesT["response"]>(
            `${domain}/dialogmessages/today/`,
            { headers: { "x-service-token": token }, params: params },
          ),
        enabled: !!domain,
        initialPageParam: 1,
        getNextPageParam: () => null,
        getPreviousPageParam: () => null,
        select: (data) => paginationReponseToGeneral(data as FixMeLater),
      });
    },
  ),

  useUpdateOneDialogMessage: typedMutation<UpdateMessageT>(() => {
    const { successToast } = useToastes();
    const queryCache = useQueryClient();

    return useMutation({
      mutationFn: (data) =>
        bareInstance.put(
          `${domain}/dialogmessages/id/${data.messageId}?messageID=${data.messageId}`,
          data,
          { headers: { "x-service-token": token } },
        ),
      onSuccess: () => {
        successToast("Message was successfully updated");
        queryCache.invalidateQueries({ queryKey: [ModuleE.DIALOG_MESSAGES] });
      },
    });
  }),

  useAddDialogMessage: typedMutation<AddDialogMessage>(() => {
    const { successToast } = useToastes();
    const queryCache = useQueryClient();

    return useMutation({
      mutationFn: (data) =>
        bareInstance.post(`${domain}/dialogmessages/`, data, {
          headers: { "x-service-token": token },
        }),
      onSuccess: () => {
        successToast("Message was successfully created");
        queryCache.invalidateQueries({ queryKey: [ModuleE.DIALOG_MESSAGES] });
      },
    });
  }),

  useChangeOrderDialogMessage: typedMutation<ChangeOrderMessageT>(() => {
    const { successToast } = useToastes();
    const queryCache = useQueryClient();

    return useMutation({
      mutationFn: ({ messageId, ...data }) =>
        bareInstance.patch(
          `${domain}/dialogmessages/order/${messageId}?messageID=${messageId}`,
          data,
          { headers: { "x-service-token": token } },
        ),
      onSuccess: () => {
        successToast("Message was successfully updated");
        queryCache.invalidateQueries({ queryKey: [ModuleE.DIALOG_MESSAGES] });
      },
    });
  }),

  useDeleteDialogMessage: typedMutation<DeleteDialogMessageT>(() => {
    const { successToast } = useToastes();
    const queryCache = useQueryClient();

    return useMutation({
      mutationFn: ({ messageId }) =>
        bareInstance.delete(
          `${domain}/dialogmessages/id/${messageId}?messageID=${messageId}`,
          { headers: { "x-service-token": token } },
        ),
      onSuccess: () => {
        successToast("Message was successfully deleted");
        queryCache.invalidateQueries({ queryKey: [ModuleE.DIALOG_MESSAGES] });
      },
    });
  }),

  useDeleteDialogMessages: typedMutation<DeleteDialogMessagesT>(() => {
    const { successToast } = useToastes();
    const queryCache = useQueryClient();

    return useMutation({
      mutationFn: ({ ids }) => {
        console.log("ids", ids);

        return bareInstance.delete(`${domain}/dialogmessages/bulk`, {
          headers: { "x-service-token": token },
          data: { ids },
        });
      },
      onSuccess: () => {
        successToast("Messages were successfully deleted");
        queryCache.invalidateQueries({ queryKey: [ModuleE.DIALOG_MESSAGES] });
      },
    });
  }),

  useApplyEditDialogMessage: typedMutation<ApplyEditDialogMessageT>(() => {
    const { successToast } = useToastes();
    const queryCache = useQueryClient();

    return useMutation({
      mutationFn: () =>
        bareInstance.get(`${domain}/dialogmessages/applyEdit/`, {
          headers: { "x-service-token": token },
        }),
      onSuccess: () => {
        successToast("Messages was successfully applied");
        queryCache.invalidateQueries({ queryKey: [ModuleE.DIALOG_MESSAGES] });
      },
    });
  }),

  useRegenerateDialogMessage: typedMutation<RegenerateDialogMessageT>(() => {
    const { successToast } = useToastes();
    const queryCache = useQueryClient();

    return useMutation({
      mutationFn: () =>
        bareInstance.get(`${domain}/dialogmessages/regenerateDialog`, {
          headers: { "x-service-token": token },
        }),
      onSuccess: () => {
        successToast("Messages was successfully regenerated");
        queryCache.invalidateQueries({ queryKey: [ModuleE.DIALOG_MESSAGES] });
      },
    });
  }),

  // ==== Control
  useSkipAllDelay: typedMutation<SkipDialogMessageT>(() => {
    const { successToast } = useToastes();
    const queryCache = useQueryClient();

    return useMutation({
      mutationFn: () =>
        bareInstance.get(`${domain}/control/skip-delay-all/`, {
          headers: { "x-service-token": token },
        }),
      onSuccess: () => {
        successToast("All delays were successfully skipped");
        queryCache.invalidateQueries({ queryKey: [ModuleE.ACTORS] });
      },
    });
  }),
});
